import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dealAnalysesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Calculator, Save, FolderOpen, Plus, Trash2, Copy, Download,
  ChevronDown, ChevronUp, TrendingUp, DollarSign,
  FileText, BarChart3, X, Loader2, Columns, Target, AlertTriangle, Layers, HelpCircle
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { HowToUseGuide } from '../components/HowToUseGuide';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);
const fmtPct = (v, dec = 2) => `${((v || 0) * 100).toFixed(dec)}%`;

// ── Reusable Inputs ──────────────────────────────────────
function CurrencyInput({ label, value, onChange, className = '', disabled = false }) {
  const display = value ? Number(value).toLocaleString('en-US') : '';
  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    onChange(raw === '' ? '' : parseFloat(raw) || 0);
  };
  return (
    <div className={className}>
      {label && <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</label>}
      <div className="relative mt-0.5">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
        <Input value={display} onChange={handleChange} disabled={disabled} className="pl-6 h-8 rounded-sm text-sm font-mono" />
      </div>
    </div>
  );
}

function PercentInput({ label, value, onChange, className = '' }) {
  return (
    <div className={className}>
      {label && <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</label>}
      <div className="relative mt-0.5">
        <Input type="number" step="0.1" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))} className="pr-6 h-8 rounded-sm text-sm font-mono" />
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">%</span>
      </div>
    </div>
  );
}

function ReadOnlyField({ label, value, accent, className = '' }) {
  return (
    <div className={className}>
      {label && <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</label>}
      <div className={`mt-0.5 h-8 flex items-center px-3 bg-muted/30 rounded-sm text-sm font-mono font-semibold ${accent ? 'text-emerald-700' : ''}`}>{value}</div>
    </div>
  );
}

function StatLine({ label, value, sub, accent, bold }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className={`text-xs ${bold ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{label}</span>
      <div className="text-right">
        <span className={`text-xs font-mono ${bold ? 'font-bold' : 'font-semibold'} ${accent ? 'text-emerald-700' : ''}`}>{value}</span>
        {sub && <span className="text-[10px] text-muted-foreground ml-1">{sub}</span>}
      </div>
    </div>
  );
}

// ── Tier Editor ──────────────────────────────────────────
function TierEditor({ tiers, setTiers, label }) {
  const add = () => setTiers([...tiers, { days_start: '', days_end: '', lf_split: '', investor_split: '' }]);
  const remove = (i) => setTiers(tiers.filter((_, idx) => idx !== i));
  const update = (i, field, val) => {
    const next = [...tiers];
    next[i] = { ...next[i], [field]: val };
    if (field === 'lf_split') { const clamped = val === '' ? '' : Math.max(0, Math.min(100, val)); next[i].lf_split = clamped; next[i].investor_split = clamped === '' ? '' : 100 - clamped; }
    if (field === 'investor_split') { const clamped = val === '' ? '' : Math.max(0, Math.min(100, val)); next[i].investor_split = clamped; next[i].lf_split = clamped === '' ? '' : 100 - clamped; }
    setTiers(next);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</h4>
        <Button size="sm" variant="outline" onClick={add} className="h-6 text-[10px] rounded-sm gap-1"><Plus className="w-3 h-3" />Tier</Button>
      </div>
      <div className="grid grid-cols-5 gap-2 mb-1 text-[10px] text-muted-foreground font-medium">
        <span>Start Day</span><span>End Day</span><span>LF Split %</span><span>Partner %</span><span></span>
      </div>
      {tiers.map((t, i) => (
        <div key={i} className="grid grid-cols-5 gap-2 mb-1.5 items-center">
          <Input type="number" value={t.days_start ?? ''} onChange={e => update(i, 'days_start', e.target.value === '' ? '' : parseInt(e.target.value))} className="h-7 text-xs rounded-sm font-mono" />
          <Input type="number" value={t.days_end ?? ''} onChange={e => update(i, 'days_end', e.target.value === '' ? '' : parseInt(e.target.value))} className="h-7 text-xs rounded-sm font-mono" placeholder="+" />
          <Input type="number" value={t.lf_split ?? ''} onChange={e => update(i, 'lf_split', e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-7 text-xs rounded-sm font-mono" />
          <Input type="number" value={t.investor_split ?? ''} onChange={e => update(i, 'investor_split', e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-7 text-xs rounded-sm font-mono" />
          <Button variant="ghost" size="sm" onClick={() => remove(i)} className="h-7 w-7 p-0"><Trash2 className="w-3 h-3 text-red-400" /></Button>
        </div>
      ))}
      {tiers.length === 0 && <p className="text-xs text-muted-foreground py-2">No tiers defined.</p>}
    </div>
  );
}

// ── Results Table ────────────────────────────────────────
function ResultsTable({ results, title, investorCapital }) {
  if (!results || results.length === 0) return null;
  const dayLabel = (s, e) => `${s}-${e === '' || e == null ? '+' : e} days`;
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{title}</h4>
      <div className="rounded-sm border overflow-hidden">
        <table className="w-full text-[11px]">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              <th className="text-left px-2.5 py-1.5 font-medium">Tier</th>
              <th className="text-right px-2.5 py-1.5 font-medium">LF %</th>
              <th className="text-right px-2.5 py-1.5 font-medium">LF Profit</th>
              <th className="text-right px-2.5 py-1.5 font-medium">Partner %</th>
              <th className="text-right px-2.5 py-1.5 font-medium">Partner Profit</th>
              <th className="text-right px-2.5 py-1.5 font-medium">CoC</th>
              <th className="text-right px-2.5 py-1.5 font-medium">Ann. ROI</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r, i) => (
              <tr key={i} className="border-t">
                <td className="px-2.5 py-1.5 font-medium">{dayLabel(r.days_start, r.days_end)}</td>
                <td className="px-2.5 py-1.5 text-right font-mono">{r.lf_split}%</td>
                <td className="px-2.5 py-1.5 text-right font-mono">{fmt(r.lf_profit)}</td>
                <td className="px-2.5 py-1.5 text-right font-mono">{r.investor_split}%</td>
                <td className="px-2.5 py-1.5 text-right font-mono">{fmt(r.investor_profit)}</td>
                <td className="px-2.5 py-1.5 text-right font-mono text-emerald-700">{fmtPct(r.coc)}</td>
                <td className="px-2.5 py-1.5 text-right font-mono text-blue-700">{r.annualized_roi != null ? fmtPct(r.annualized_roi) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Collapsible Section ──────────────────────────────────
function Section({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="rounded-sm">
      <CardHeader className="py-2.5 px-4 cursor-pointer select-none" onClick={() => setOpen(!open)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Icon className="w-4 h-4 text-muted-foreground" /><CardTitle className="text-sm">{title}</CardTitle></div>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </CardHeader>
      {open && <CardContent className="px-4 pb-4 pt-0">{children}</CardContent>}
    </Card>
  );
}

// ── Default State ────────────────────────────────────────
const DEFAULT_DEAL = {
  purchase_price: '', survey: '', soil: '', other_dd: '',
  purchase_closing_costs: '', improvements: '',
  sale_price: '', sale_closing_costs: '', sale_commission_pct: 6,
};
const DEFAULT_FUNDING = {
  lf_pct_purchase: 0, lf_pct_dd: 0, lf_pct_improvements: 0, lf_pct_closing: 50,
  preferred_return: 0,
};
// Scenario A — standard 5-tier investor schedule (40/45/50/55/60).
// Investor % goes UP as hold time grows (incentivizes patient capital).
const DEFAULT_TIERS_A = [
  { days_start: 1,   days_end: 180,  lf_split: 60, investor_split: 40 },
  { days_start: 181, days_end: 270,  lf_split: 55, investor_split: 45 },
  { days_start: 271, days_end: 365,  lf_split: 50, investor_split: 50 },
  { days_start: 366, days_end: 456,  lf_split: 45, investor_split: 55 },
  { days_start: 457, days_end: '',   lf_split: 40, investor_split: 60 },
];
// Scenario B — less generous 4-tier alternative offer (25/35/45/55).
const DEFAULT_TIERS_B = [
  { days_start: 1,   days_end: 180,  lf_split: 75, investor_split: 25 },
  { days_start: 181, days_end: 270,  lf_split: 65, investor_split: 35 },
  { days_start: 271, days_end: 365,  lf_split: 55, investor_split: 45 },
  { days_start: 366, days_end: '',   lf_split: 45, investor_split: 55 },
];

// ── Main Component ───────────────────────────────────────
export default function DealAnalysis() {
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [name, setName] = useState('Untitled Analysis');
  const [notes, setNotes] = useState('');
  const [deal, setDeal] = useState({ ...DEFAULT_DEAL });
  const [funding, setFunding] = useState({ ...DEFAULT_FUNDING });
  const [optionATiers, setOptionATiers] = useState([...DEFAULT_TIERS_A]);
  const [optionBTiers, setOptionBTiers] = useState([...DEFAULT_TIERS_B]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSaved, setShowSaved] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [compareId, setCompareId] = useState(null);
  const [acreage, setAcreage] = useState('');
  const [isSubdivide, setIsSubdivide] = useState(false);
  // Default subdivide allocation by Acres; user can flip to Percent.
  const [subdivideMode, setSubdivideMode] = useState('acres');
  const [parcels, setParcels] = useState([]);
  const [isDirty, setIsDirty] = useState(false);

  // Mark dirty on any input change
  useEffect(() => { setIsDirty(true); }, [name, notes, deal, funding, optionATiers, optionBTiers, acreage, isSubdivide, subdivideMode, parcels]);

  const loadAnalyses = useCallback(async () => {
    try {
      const res = await dealAnalysesApi.list();
      setAnalyses(res.data);
    } catch (err) {
      console.error('Failed to load deal analyses:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadAnalyses(); }, [loadAnalyses]);

  // ── Calculations ─────────────────────────────────────
  const calcs = useMemo(() => {
    const pp = parseFloat(deal.purchase_price) || 0;
    const survey = parseFloat(deal.survey) || 0;
    const soil = parseFloat(deal.soil) || 0;
    const otherDD = parseFloat(deal.other_dd) || 0;
    const totalDD = survey + soil + otherDD;
    const purchaseClosing = parseFloat(deal.purchase_closing_costs) || 0;
    const improvements = parseFloat(deal.improvements) || 0;
    const totalAcquisitionCost = pp + totalDD + purchaseClosing + improvements;

    const sp = parseFloat(deal.sale_price) || 0;
    const saleClosing = parseFloat(deal.sale_closing_costs) || 0;
    const saleCommPct = parseFloat(deal.sale_commission_pct) || 0;
    const saleCommission = sp * (saleCommPct / 100);
    const grossSalesRevenue = sp - saleClosing - saleCommission;
    const grossProfit = grossSalesRevenue - totalAcquisitionCost;
    const margin = sp > 0 ? grossProfit / sp : 0;

    const lfPurchase = pp * ((parseFloat(funding.lf_pct_purchase) || 0) / 100);
    const lfDD = totalDD * ((parseFloat(funding.lf_pct_dd) || 0) / 100);
    const lfImprovements = improvements * ((parseFloat(funding.lf_pct_improvements) || 0) / 100);
    const lfClosing = purchaseClosing * ((parseFloat(funding.lf_pct_closing) || 0) / 100);
    const lfTotalInvestment = lfPurchase + lfDD + lfImprovements + lfClosing;
    const investorTotalInvestment = totalAcquisitionCost - lfTotalInvestment;

    return {
      pp, survey, soil, otherDD, totalDD, purchaseClosing, improvements, totalAcquisitionCost,
      sp, saleClosing, saleCommPct, saleCommission, grossSalesRevenue, grossProfit, margin,
      lfPurchase, lfDD, lfImprovements, lfClosing, lfTotalInvestment, investorTotalInvestment,
      pricePerAcrePurchase: (parseFloat(acreage) || 0) > 0 ? pp / parseFloat(acreage) : 0,
      pricePerAcreSale: (parseFloat(acreage) || 0) > 0 ? sp / parseFloat(acreage) : 0,
    };
  }, [deal, funding, acreage]);

  const computeTierResults = useCallback((tiers) => {
    return tiers.map(t => {
      const lfPct = (parseFloat(t.lf_split) || 0) / 100;
      const invPct = (parseFloat(t.investor_split) || 0) / 100;
      const lf_profit = calcs.grossProfit * lfPct;
      const investor_profit = calcs.grossProfit * invPct;
      const coc = calcs.investorTotalInvestment > 0 ? investor_profit / calcs.investorTotalInvestment : 0;
      const dEnd = t.days_end === '' || t.days_end == null ? null : parseInt(t.days_end);
      const annualized_roi = dEnd && dEnd > 0 ? coc * (365 / dEnd) : null;
      return { ...t, lf_profit, investor_profit, coc, annualized_roi };
    });
  }, [calcs]);

  const optionAResults = useMemo(() => computeTierResults(optionATiers), [optionATiers, computeTierResults]);
  const optionBResults = useMemo(() => computeTierResults(optionBTiers), [optionBTiers, computeTierResults]);

  // ── Subdivide ──────────────────────────────────────────
  const addParcel = () => {
    const n = parcels.length + 1;
    setParcels(prev => [...prev, { name: `Parcel ${n}`, acres: '', pct: '', price_per_acre: '', sale_price: '', closing_costs: '', commission_pct: '', days_held: '', is_jv: true }]);
  };
  const removeParcel = (i) => setParcels(prev => prev.filter((_, idx) => idx !== i));
  const updateParcel = (index, field, value) => {
    setParcels(prev => prev.map((p, i) => {
      if (i !== index) return p;
      const u = { ...p, [field]: value };
      const a = parseFloat(u.acres) || 0;
      if (field === 'price_per_acre' && a > 0) u.sale_price = Math.round((parseFloat(value) || 0) * a);
      else if (field === 'sale_price' && a > 0) u.price_per_acre = Math.round(((parseFloat(value) || 0) / a) * 100) / 100;
      else if (field === 'acres' && parseFloat(u.price_per_acre) > 0) u.sale_price = Math.round(parseFloat(u.price_per_acre) * (parseFloat(value) || 0));
      return u;
    }));
  };

  // Cost-allocation ratio for a parcel: by acres OR by percent.
  const parcelCostRatio = (p, totalAcres) => {
    if (subdivideMode === 'percent') {
      return (parseFloat(p.pct) || 0) / 100;
    }
    return totalAcres > 0 ? (parseFloat(p.acres) || 0) / totalAcres : 0;
  };

  const parcelResults = useMemo(() => {
    if (!isSubdivide || !parcels.length) return [];
    const totalAcres = parseFloat(acreage) || 0;
    const mainClosing = parseFloat(deal.sale_closing_costs) || 0;
    return parcels.map(p => {
      const pAcres = parseFloat(p.acres) || 0;
      const pSP = parseFloat(p.sale_price) || 0;
      const pCommPct = p.commission_pct !== '' ? parseFloat(p.commission_pct) : calcs.saleCommPct;
      const pComm = pSP * (pCommPct / 100);
      const costRatio = parcelCostRatio(p, totalAcres);
      const pClosing = p.closing_costs !== '' ? (parseFloat(p.closing_costs) || 0) : costRatio * mainClosing;
      const pRevenue = pSP - pComm - pClosing;
      const costAlloc = costRatio * calcs.totalAcquisitionCost;
      const gp = pRevenue - costAlloc;
      const days = parseInt(p.days_held) || 0;
      let tier = optionATiers.find(t => {
        const s = parseInt(t.days_start) || 0;
        const e = t.days_end === '' || t.days_end == null ? Infinity : parseInt(t.days_end);
        return days >= s && days <= e;
      });
      if (!tier && optionATiers.length > 0) tier = optionATiers[0];
      let lfPct = 100, partnerPct = 0;
      if (p.is_jv && calcs.investorTotalInvestment > 0 && tier) {
        lfPct = parseFloat(tier.lf_split) || 0;
        partnerPct = parseFloat(tier.investor_split) || 0;
      }
      return { ...p, pAcres, costRatio, revenue: pRevenue, commission: pComm, commPct: pCommPct, closingCosts: pClosing, costAlloc, grossProfit: gp, lfPct, partnerPct, lfProfit: gp * (lfPct / 100), partnerProfit: gp * (partnerPct / 100), tierLabel: tier ? `${tier.days_start}-${tier.days_end || '+'}d` : '—', hasDays: (parseInt(p.days_held) || 0) > 0 };
    });
  }, [isSubdivide, parcels, acreage, calcs, optionATiers, deal.sale_closing_costs, subdivideMode]);

  const subdivSummary = useMemo(() => {
    if (!parcelResults.length) return null;
    return {
      totalAcres: parcelResults.reduce((s, p) => s + p.pAcres, 0),
      totalSalePrice: parcelResults.reduce((s, p) => s + (parseFloat(p.sale_price) || 0), 0),
      totalClosing: parcelResults.reduce((s, p) => s + (p.closingCosts || 0), 0),
      totalRevenue: parcelResults.reduce((s, p) => s + p.revenue, 0),
      totalGP: parcelResults.reduce((s, p) => s + p.grossProfit, 0),
      totalLF: parcelResults.reduce((s, p) => s + p.lfProfit, 0),
      totalPartner: parcelResults.reduce((s, p) => s + p.partnerProfit, 0),
    };
  }, [parcelResults]);

  // Scenario B parcel results for comparison
  const parcelResultsB = useMemo(() => {
    if (!isSubdivide || !parcels.length) return [];
    const totalAcres = parseFloat(acreage) || 0;
    const mainClosing = parseFloat(deal.sale_closing_costs) || 0;
    return parcels.map(p => {
      const pAcres = parseFloat(p.acres) || 0;
      const pSP = parseFloat(p.sale_price) || 0;
      const pCommPct = p.commission_pct !== '' ? parseFloat(p.commission_pct) : calcs.saleCommPct;
      const pComm = pSP * (pCommPct / 100);
      const costRatio = parcelCostRatio(p, totalAcres);
      const pClosing = p.closing_costs !== '' ? (parseFloat(p.closing_costs) || 0) : costRatio * mainClosing;
      const pRevenue = pSP - pComm - pClosing;
      const costAlloc = costRatio * calcs.totalAcquisitionCost;
      const gp = pRevenue - costAlloc;
      const days = parseInt(p.days_held) || 0;
      let tier = optionBTiers.find(t => {
        const s = parseInt(t.days_start) || 0;
        const e = t.days_end === '' || t.days_end == null ? Infinity : parseInt(t.days_end);
        return days >= s && days <= e;
      });
      if (!tier && optionBTiers.length > 0) tier = optionBTiers[0];
      let lfPct = 100, partnerPct = 0;
      if (p.is_jv && calcs.investorTotalInvestment > 0 && tier) {
        lfPct = parseFloat(tier.lf_split) || 0;
        partnerPct = parseFloat(tier.investor_split) || 0;
      }
      return { ...p, pAcres, revenue: pRevenue, costAlloc, grossProfit: gp, lfPct, partnerPct, lfProfit: gp * (lfPct / 100), partnerProfit: gp * (partnerPct / 100), tierLabel: tier ? `${tier.days_start}-${tier.days_end || '+'}d` : '—' };
    });
  }, [isSubdivide, parcels, acreage, calcs, optionBTiers, deal.sale_closing_costs, subdivideMode]);

  const subdivSummaryB = useMemo(() => {
    if (!parcelResultsB.length) return null;
    return {
      totalLF: parcelResultsB.reduce((s, p) => s + p.lfProfit, 0),
      totalPartner: parcelResultsB.reduce((s, p) => s + p.partnerProfit, 0),
      totalGP: parcelResultsB.reduce((s, p) => s + p.grossProfit, 0),
    };
  }, [parcelResultsB]);

  // ── Tier Validation ────────────────────────────────────
  const tierWarnings = useMemo(() => {
    const check = (tiers, label) => {
      const warnings = [];
      for (let i = 0; i < tiers.length; i++) {
        const s = parseInt(tiers[i].days_start) || 0;
        const e = tiers[i].days_end === '' || tiers[i].days_end == null ? Infinity : parseInt(tiers[i].days_end);
        if (e !== Infinity && e < s) warnings.push(`${label} Tier ${i + 1}: End day (${e}) is before start day (${s})`);
        if (i > 0) {
          const prevE = tiers[i - 1].days_end === '' || tiers[i - 1].days_end == null ? Infinity : parseInt(tiers[i - 1].days_end);
          if (prevE !== Infinity && s > prevE + 1) warnings.push(`${label}: Gap between Tier ${i} (ends ${prevE}) and Tier ${i + 1} (starts ${s})`);
          if (prevE !== Infinity && s <= prevE) warnings.push(`${label}: Overlap between Tier ${i} (ends ${prevE}) and Tier ${i + 1} (starts ${s})`);
        }
      }
      return warnings;
    };
    return [...check(optionATiers, 'Scenario A'), ...check(optionBTiers, 'Scenario B')];
  }, [optionATiers, optionBTiers]);

  // ── Parcel Acres Validation ─────────────────────────────
  const parcelAcresTotal = useMemo(() => parcels.reduce((s, p) => s + (parseFloat(p.acres) || 0), 0), [parcels]);
  const totalAcresNum = parseFloat(acreage) || 0;
  const acresMismatch = isSubdivide && parcels.length > 0 && totalAcresNum > 0 && Math.abs(parcelAcresTotal - totalAcresNum) > 0.01;

  const setDealField = (f, v) => setDeal(prev => ({ ...prev, [f]: v }));
  const setFundingField = (f, v) => setFunding(prev => ({ ...prev, [f]: v }));

  // ── Goal Seek ──────────────────────────────────────────
  const [goalType, setGoalType] = useState('margin');
  const [goalValue, setGoalValue] = useState('');
  const [solveFor, setSolveFor] = useState('sale_price');
  const [goalTierIndex, setGoalTierIndex] = useState(0);

  const goalResult = useMemo(() => {
    const target = parseFloat(goalValue) || 0;
    if (target <= 0) return null;

    const tac = calcs.totalAcquisitionCost;
    const scc = parseFloat(deal.sale_closing_costs) || 0;
    const scPct = calcs.saleCommPct / 100;
    const sp = calcs.sp;
    const totalDD = calcs.totalDD;
    const purchaseClosing = calcs.purchaseClosing;
    const improvements = calcs.improvements;

    const lfPctPP = (parseFloat(funding.lf_pct_purchase) || 0) / 100;
    const lfPctDD = (parseFloat(funding.lf_pct_dd) || 0) / 100;
    const lfPctImp = (parseFloat(funding.lf_pct_improvements) || 0) / 100;
    const lfPctClose = (parseFloat(funding.lf_pct_closing) || 0) / 100;
    const selectedTier = optionATiers[goalTierIndex] || optionATiers[0];
    const partnerSplitPct = selectedTier ? (parseFloat(selectedTier.investor_split) || 0) / 100 : 0.4;

    if (solveFor === 'sale_price') {
      if (goalType === 'margin') {
        const m = target / 100;
        const denom = 1 - scPct - m;
        if (denom <= 0) return { error: 'Target margin too high for current commission rate' };
        return { value: (scc + tac) / denom, label: 'Required Sale Price' };
      }
      let requiredGP;
      if (goalType === 'profit') {
        requiredGP = target;
      } else {
        const partnerInv = calcs.investorTotalInvestment;
        if (partnerInv <= 0) return { error: 'No partner investment configured' };
        if (partnerSplitPct <= 0) return { error: 'No partner split configured' };
        requiredGP = (target / 100) * partnerInv / partnerSplitPct;
      }
      const denom = 1 - scPct;
      if (denom <= 0) return { error: 'Sale commission is 100% or more' };
      return { value: (requiredGP + scc + tac) / denom, label: 'Required Sale Price' };

    } else {
      const netSaleProceeds = sp * (1 - scPct) - scc;
      if (goalType === 'profit') {
        const maxPP = netSaleProceeds - totalDD - purchaseClosing - improvements - target;
        return { value: Math.max(0, maxPP), label: 'Maximum Purchase Price' };
      } else if (goalType === 'margin') {
        const requiredGP = sp * (target / 100);
        const maxPP = netSaleProceeds - totalDD - purchaseClosing - improvements - requiredGP;
        return { value: Math.max(0, maxPP), label: 'Maximum Purchase Price' };
      } else {
        const C = totalDD * (1 - lfPctDD) + improvements * (1 - lfPctImp) + purchaseClosing * (1 - lfPctClose);
        const R = netSaleProceeds - totalDD - purchaseClosing - improvements;
        const t = target / 100;
        const s = partnerSplitPct;
        const a = 1 - lfPctPP;
        if (s <= 0) return { error: 'No partner split configured' };
        const denom = s + t * a;
        if (denom <= 0) return { error: 'Cannot solve — target too high' };
        const maxPP = (R * s - t * C) / denom;
        return { value: Math.max(0, maxPP), label: 'Maximum Purchase Price' };
      }
    }
  }, [goalType, goalValue, solveFor, calcs, deal, funding, optionATiers, goalTierIndex]);

  const goalSummary = useMemo(() => {
    if (!goalResult || goalResult.error) return null;
    const rv = goalResult.value;
    const newSP = solveFor === 'sale_price' ? rv : calcs.sp;
    const newPP = solveFor === 'purchase_price' ? rv : calcs.pp;
    const scPct = calcs.saleCommPct / 100;
    const scc = parseFloat(deal.sale_closing_costs) || 0;
    const newTAC = newPP + calcs.totalDD + calcs.purchaseClosing + calcs.improvements;
    const newGP = (newSP - scc - newSP * scPct) - newTAC;
    const newMargin = newSP > 0 ? newGP / newSP : 0;
    const newLFInv = newPP * ((parseFloat(funding.lf_pct_purchase) || 0) / 100) + calcs.totalDD * ((parseFloat(funding.lf_pct_dd) || 0) / 100) + calcs.improvements * ((parseFloat(funding.lf_pct_improvements) || 0) / 100) + calcs.purchaseClosing * ((parseFloat(funding.lf_pct_closing) || 0) / 100);
    const newPartnerInv = newTAC - newLFInv;
    const tier = optionATiers[goalTierIndex] || optionATiers[0];
    const pSplit = tier ? (parseFloat(tier.investor_split) || 0) / 100 : 0.4;
    const lSplit = tier ? (parseFloat(tier.lf_split) || 0) / 100 : 0.6;
    return { gp: newGP, margin: newMargin, coc: newPartnerInv > 0 ? (newGP * pSplit) / newPartnerInv : 0, partnerProfit: newGP * pSplit, lfProfit: newGP * lSplit, partnerInv: newPartnerInv };
  }, [goalResult, solveFor, calcs, deal, funding, optionATiers, goalTierIndex]);

  const applyGoalResult = () => {
    if (!goalResult || goalResult.error) return;
    if (solveFor === 'sale_price') setDealField('sale_price', Math.round(goalResult.value));
    else setDealField('purchase_price', Math.round(goalResult.value));
    toast.success(`Applied ${goalResult.label}: ${fmt(goalResult.value)}`);
  };

  const loadAnalysis = (a) => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard and load?')) return;
    setActiveId(a.id);
    setName(a.name);
    setNotes(a.notes || '');
    setDeal(a.deal || { ...DEFAULT_DEAL });
    setFunding(a.funding || { ...DEFAULT_FUNDING });
    setOptionATiers(a.option_a_tiers?.length ? a.option_a_tiers : [...DEFAULT_TIERS_A]);
    setOptionBTiers(a.option_b_tiers?.length ? a.option_b_tiers : [...DEFAULT_TIERS_B]);
    setAcreage(a.acreage || '');
    setIsSubdivide(a.is_subdivide || false);
    setSubdivideMode(a.subdivide_mode || 'acres');
    setParcels(a.parcels || []);
    setShowSaved(false);
    setIsDirty(false);
    toast.success(`Loaded "${a.name}"`);
  };

  const handleNew = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard and start new?')) return;
    setActiveId(null);
    setName('Untitled Analysis');
    setNotes('');
    setDeal({ ...DEFAULT_DEAL });
    setFunding({ ...DEFAULT_FUNDING });
    setOptionATiers([...DEFAULT_TIERS_A]);
    setOptionBTiers([...DEFAULT_TIERS_B]);
    setAcreage('');
    setIsSubdivide(false);
    setSubdivideMode('acres');
    setParcels([]);
    setIsDirty(false);
  };

  const handlePushToProposal = () => {
    // Map Deal Analysis fields → Proposal Calculator fields
    const proposalData = {
      scenarioName: name || 'Untitled',
      deal: {
        purchasePrice: parseFloat(deal.purchase_price) || 0,
        survey: parseFloat(deal.survey) || 0,
        soil: parseFloat(deal.soil) || 0,
        otherDD: parseFloat(deal.other_dd) || 0,
        purchaseClosingCosts: parseFloat(deal.purchase_closing_costs) || 0,
        improvements: parseFloat(deal.improvements) || 0,
        estimatedSalePrice: parseFloat(deal.sale_price) || 0,
        saleClosingCosts: parseFloat(deal.sale_closing_costs) || 0,
        saleCommission: parseFloat(deal.sale_commission_pct) || 6,
      },
      funding: {
        lfPurchasePrice: parseFloat(funding.lf_pct_purchase) || 0,
        lfDDCosts: parseFloat(funding.lf_pct_dd) || 0,
        lfImprovements: parseFloat(funding.lf_pct_improvements) || 0,
        lfClosingCosts: parseFloat(funding.lf_pct_closing) || 0,
      },
      optionATiers: optionATiers.map(t => ({
        startDay: t.days_start,
        endDay: t.days_end,
        investorSplit: t.investor_split,
      })),
      optionBTiers: optionBTiers.map(t => ({
        startDay: t.days_start,
        endDay: t.days_end,
        investorSplit: t.investor_split,
      })),
      isSubdivide,
      parcels,
    };
    sessionStorage.setItem('pushToProposal', JSON.stringify(proposalData));
    toast.success('Pushing to Proposal...');
    navigate('/proposals');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { name, notes, deal, funding, option_a_tiers: optionATiers, option_b_tiers: optionBTiers, acreage, is_subdivide: isSubdivide, subdivide_mode: subdivideMode, parcels };
      if (activeId) { await dealAnalysesApi.update(activeId, payload); toast.success('Analysis saved'); }
      else { const res = await dealAnalysesApi.create(payload); setActiveId(res.data.id); toast.success('Analysis created'); }
      loadAnalyses();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
    setIsDirty(false);
  };

  const handleDelete = async (id) => {
    try { await dealAnalysesApi.delete(id); if (activeId === id) handleNew(); if (compareId === id) setCompareId(null); loadAnalyses(); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  const handleDuplicate = async (id) => {
    try { await dealAnalysesApi.duplicate(id); loadAnalyses(); toast.success('Duplicated'); } catch { toast.error('Failed'); }
  };

  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfScenarios, setPdfScenarios] = useState([]);

  const handleDownloadPDF = () => {
    setShowPdfDialog(true);
    setPdfScenarios([]);
  };

  const confirmDownloadPDF = () => {
    if (pdfScenarios.length === 0) {
      toast.error('Select at least one scenario');
      return;
    }
    const includeA = pdfScenarios.includes('A');
    const includeB = pdfScenarios.includes('B');
    const includeBoth = includeA && includeB;
    try {
      const doc = new jsPDF();
      const pw = doc.internal.pageSize.getWidth();
      const FOREST = [26, 47, 35]; const TERRA = [198, 142, 93]; const LIGHT = [245, 245, 240];

      // Header
      doc.setFillColor(...FOREST); doc.rect(0, 0, pw, 32, 'F');
      doc.setFillColor(...TERRA); doc.rect(0, 32, pw, 1.5, 'F');
      doc.setTextColor(255, 255, 255); doc.setFontSize(18); doc.text(name || 'Deal Analysis', 14, 17);
      doc.setFontSize(10); doc.text(`Land Finder  |  ${new Date().toLocaleDateString()}`, 14, 26);

      let y = 42;
      doc.setTextColor(0, 0, 0);

      const summaryRowStyle = { fillColor: LIGHT, fontStyle: 'bold' };

      // Acquisition Costs
      doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.text('Acquisition Costs', 14, y); y += 8;
      const acqBody = [
        ['Purchase Price', fmt(calcs.pp)],
        ['Survey', fmt(calcs.survey)], ['Soil', fmt(calcs.soil)], ['Other DD', fmt(calcs.otherDD)],
        ['Total Due Diligence', fmt(calcs.totalDD)],
        ['Purchase Closing Costs', fmt(calcs.purchaseClosing)],
        ['Improvements', fmt(calcs.improvements)],
        [{ content: 'Total Acquisition Cost', styles: summaryRowStyle }, { content: fmt(calcs.totalAcquisitionCost), styles: summaryRowStyle }],
      ];
      if ((parseFloat(acreage) || 0) > 0) {
        acqBody.unshift(['Total Acreage', `${Number(acreage).toFixed(2)} acres`]);
        acqBody.push(['Purchase Price / Acre', fmt(calcs.pricePerAcrePurchase)]);
      }
      autoTable(doc, {
        startY: y, margin: { left: 14, right: 14 }, theme: 'grid', headStyles: { fillColor: FOREST, fontSize: 8 }, bodyStyles: { fontSize: 8 },
        head: [['Item', 'Amount']],
        body: acqBody,
      });
      y = doc.lastAutoTable.finalY + 10;

      // Sale & Revenue
      doc.setFontSize(12); doc.text('Sale & Revenue', 14, y); y += 8;
      const saleBody = [
        ['Sale Price', fmt(calcs.sp)],
        ['Sale Closing Costs', `-${fmt(calcs.saleClosing)}`],
        [`Sale Commission (${calcs.saleCommPct}%)`, `-${fmt(calcs.saleCommission)}`],
        [{ content: 'Gross Sales Revenue', styles: summaryRowStyle }, { content: fmt(calcs.grossSalesRevenue), styles: summaryRowStyle }],
        [{ content: 'Gross Profit', styles: { fillColor: [220, 237, 225], fontStyle: 'bold' } }, { content: fmt(calcs.grossProfit), styles: { fillColor: [220, 237, 225], fontStyle: 'bold' } }],
        ['Margin', fmtPct(calcs.margin)],
      ];
      if ((parseFloat(acreage) || 0) > 0 && calcs.sp > 0) saleBody.push(['Sale Price / Acre', fmt(calcs.pricePerAcreSale)]);
      autoTable(doc, {
        startY: y, margin: { left: 14, right: 14 }, theme: 'grid', headStyles: { fillColor: FOREST, fontSize: 8 }, bodyStyles: { fontSize: 8 },
        head: [['Item', 'Amount']],
        body: saleBody,
      });
      y = doc.lastAutoTable.finalY + 10;

      // Funding
      doc.setFontSize(12); doc.text('Funding Structure', 14, y); y += 8;
      autoTable(doc, {
        startY: y, margin: { left: 14, right: 14 }, theme: 'grid', headStyles: { fillColor: FOREST, fontSize: 8 }, bodyStyles: { fontSize: 8 },
        head: [['Category', 'LF %', 'LF Amount', 'Partner Amount']],
        body: [
          ['Purchase Price', `${funding.lf_pct_purchase || 0}%`, fmt(calcs.lfPurchase), fmt(calcs.pp - calcs.lfPurchase)],
          ['DD Costs', `${funding.lf_pct_dd || 0}%`, fmt(calcs.lfDD), fmt(calcs.totalDD - calcs.lfDD)],
          ['Improvements', `${funding.lf_pct_improvements || 0}%`, fmt(calcs.lfImprovements), fmt(calcs.improvements - calcs.lfImprovements)],
          ['Closing Costs', `${funding.lf_pct_closing || 0}%`, fmt(calcs.lfClosing), fmt(calcs.purchaseClosing - calcs.lfClosing)],
          [
            { content: 'Total', styles: summaryRowStyle },
            { content: '', styles: summaryRowStyle },
            { content: fmt(calcs.lfTotalInvestment), styles: summaryRowStyle },
            { content: fmt(calcs.investorTotalInvestment), styles: summaryRowStyle },
          ],
        ],
      });
      y = doc.lastAutoTable.finalY + 10;

      // Tiers
      const tierBody = (results) => results.map(r => [
        `${r.days_start}-${r.days_end === '' || r.days_end == null ? '+' : r.days_end} days`,
        `${r.lf_split}%`, fmt(r.lf_profit), `${r.investor_split}%`, fmt(r.investor_profit), fmtPct(r.coc), r.annualized_roi != null ? fmtPct(r.annualized_roi) : '—',
      ]);

      if (includeA && optionAResults.length) {
        if (y > 240) { doc.addPage(); y = 20; }
        doc.setFontSize(12); doc.text(includeBoth ? 'Scenario A — Profit Split' : 'Profit Split', 14, y); y += 8;
        autoTable(doc, {
          startY: y, margin: { left: 14, right: 14 }, theme: 'grid', headStyles: { fillColor: FOREST, fontSize: 7 }, bodyStyles: { fontSize: 7 },
          head: [['Tier', 'LF %', 'LF Profit', 'Partner %', 'Partner Profit', 'CoC', 'Ann. ROI']],
          body: tierBody(optionAResults),
        });
        y = doc.lastAutoTable.finalY + 10;
      }
      if (includeB && optionBResults.length) {
        if (y > 240) { doc.addPage(); y = 20; }
        doc.setFontSize(12); doc.text(includeBoth ? 'Scenario B — Profit Split' : 'Profit Split', 14, y); y += 8;
        autoTable(doc, {
          startY: y, margin: { left: 14, right: 14 }, theme: 'grid', headStyles: { fillColor: FOREST, fontSize: 7 }, bodyStyles: { fontSize: 7 },
          head: [['Tier', 'LF %', 'LF Profit', 'Partner %', 'Partner Profit', 'CoC', 'Ann. ROI']],
          body: tierBody(optionBResults),
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      // Subdivide Breakdown
      if (isSubdivide && parcelResults.length > 0) {
        if (y > 200) { doc.addPage(); y = 20; }
        doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.text('Subdivide Analysis', 14, y); y += 8;
        autoTable(doc, {
          startY: y, margin: { left: 14, right: 14 }, theme: 'grid', headStyles: { fillColor: TERRA, fontSize: 7, textColor: [255, 255, 255] }, bodyStyles: { fontSize: 7 },
          head: [['Parcel', 'Acres', '$/Acre', 'Sale Price', 'Closing', 'Revenue', 'Cost Alloc.', 'Profit', 'LF Profit', 'Partner', 'Tier']],
          body: [
            ...parcelResults.map(r => [
              r.name + (r.is_jv ? '' : ' (No JV)'), String(r.pAcres), fmt(r.pAcres > 0 ? (parseFloat(r.sale_price) || 0) / r.pAcres : 0),
              fmt(parseFloat(r.sale_price) || 0), fmt(r.closingCosts || 0), fmt(r.revenue), fmt(r.costAlloc), fmt(r.grossProfit), fmt(r.lfProfit), fmt(r.partnerProfit), r.tierLabel,
            ]),
            [
              { content: 'Total', styles: summaryRowStyle }, { content: String(subdivSummary?.totalAcres?.toFixed(2) || ''), styles: summaryRowStyle }, { content: '', styles: summaryRowStyle },
              { content: fmt(subdivSummary?.totalSalePrice || 0), styles: summaryRowStyle }, { content: fmt(subdivSummary?.totalClosing || 0), styles: summaryRowStyle },
              { content: fmt(subdivSummary?.totalRevenue || 0), styles: summaryRowStyle },
              { content: fmt(calcs.totalAcquisitionCost), styles: summaryRowStyle }, { content: fmt(subdivSummary?.totalGP || 0), styles: summaryRowStyle },
              { content: fmt(subdivSummary?.totalLF || 0), styles: summaryRowStyle }, { content: fmt(subdivSummary?.totalPartner || 0), styles: summaryRowStyle },
              { content: '', styles: summaryRowStyle },
            ],
          ],
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      // Scenario Comparison (only when both selected)
      if (includeBoth && isSubdivide && parcelResults.length > 0 && parcelResultsB.length > 0) {
        if (y > 180) { doc.addPage(); y = 20; }
        doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.text('Scenario Comparison', 14, y); y += 8;
        const compBody = parcelResults.map((rA, i) => {
          const rB = parcelResultsB[i];
          return [
            rA.name + (rA.is_jv ? '' : ' (No JV)'), String(rA.pAcres), String(rA.days_held || '—'),
            rA.tierLabel, fmt(rA.lfProfit), fmt(rA.partnerProfit),
            rB?.tierLabel || '—', fmt(rB?.lfProfit || 0), fmt(rB?.partnerProfit || 0),
          ];
        });
        const aTotalLF = subdivSummary?.totalLF || 0;
        const bTotalLF = subdivSummaryB?.totalLF || 0;
        const aTotalP = subdivSummary?.totalPartner || 0;
        const bTotalP = subdivSummaryB?.totalPartner || 0;
        compBody.push([
          { content: 'Totals', styles: summaryRowStyle }, { content: '', styles: summaryRowStyle }, { content: '', styles: summaryRowStyle },
          { content: '', styles: summaryRowStyle }, { content: fmt(aTotalLF), styles: summaryRowStyle }, { content: fmt(aTotalP), styles: summaryRowStyle },
          { content: '', styles: summaryRowStyle }, { content: fmt(bTotalLF), styles: summaryRowStyle }, { content: fmt(bTotalP), styles: summaryRowStyle },
        ]);
        autoTable(doc, {
          startY: y, margin: { left: 14, right: 14 }, theme: 'grid', bodyStyles: { fontSize: 7 },
          headStyles: { fillColor: FOREST, fontSize: 7, textColor: [255, 255, 255] },
          head: [['Parcel', 'Acres', 'Days', 'A Tier', 'A LF Profit', 'A Partner', 'B Tier', 'B LF Profit', 'B Partner']],
          body: compBody,
          columnStyles: { 3: { fillColor: [240, 245, 240] }, 4: { fillColor: [240, 245, 240] }, 5: { fillColor: [240, 245, 240] }, 6: { fillColor: [252, 245, 238] }, 7: { fillColor: [252, 245, 238] }, 8: { fillColor: [252, 245, 238] } },
        });
        y = doc.lastAutoTable.finalY + 4;
        const winner = aTotalLF >= bTotalLF ? 'Scenario A' : 'Scenario B';
        const diff = Math.abs(aTotalLF - bTotalLF);
        doc.setFontSize(9); doc.setFont(undefined, 'bold');
        doc.text(`LF Profit Advantage: ${winner} by ${fmt(diff)}`, 14, y + 4);
        y += 12;
      }

      // Notes
      if (notes && notes.trim()) {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.text('Notes', 14, y); y += 7;
        doc.setFontSize(9); doc.setFont(undefined, 'normal');
        const lines = doc.splitTextToSize(notes, pw - 28);
        doc.text(lines, 14, y);
        y += lines.length * 4.5;
      }

      // Footer on all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8); doc.setTextColor(150, 150, 150);
        doc.text(`Land Finder  |  Confidential`, 14, doc.internal.pageSize.getHeight() - 10);
        doc.text(`Page ${i} of ${pageCount}`, pw - 14, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
      }

      doc.save(`${(name || 'analysis').replace(/\s+/g, '_')}.pdf`);
      toast.success('PDF downloaded');
      setShowPdfDialog(false);
    } catch (e) { console.error(e); toast.error('PDF generation failed'); }
  };

  // Compare
  const compareAnalysis = useMemo(() => analyses.find(a => a.id === compareId), [analyses, compareId]);
  const compareCalcs = useMemo(() => {
    if (!compareAnalysis) return null;
    const d = compareAnalysis.deal || {};
    const f = compareAnalysis.funding || {};
    const pp = parseFloat(d.purchase_price) || 0;
    const tDD = (parseFloat(d.survey) || 0) + (parseFloat(d.soil) || 0) + (parseFloat(d.other_dd) || 0);
    const pClose = parseFloat(d.purchase_closing_costs) || 0;
    const impr = parseFloat(d.improvements) || 0;
    const totalAcq = pp + tDD + pClose + impr;
    const sp = parseFloat(d.sale_price) || 0;
    const sClose = parseFloat(d.sale_closing_costs) || 0;
    const sCommPct = parseFloat(d.sale_commission_pct) || 0;
    const sComm = sp * (sCommPct / 100);
    const gsr = sp - sClose - sComm;
    const gp = gsr - totalAcq;
    const lfInv = pp * ((parseFloat(f.lf_pct_purchase) || 0) / 100) + tDD * ((parseFloat(f.lf_pct_dd) || 0) / 100) + impr * ((parseFloat(f.lf_pct_improvements) || 0) / 100) + pClose * ((parseFloat(f.lf_pct_closing) || 0) / 100);
    const invInv = totalAcq - lfInv;
    return { pp, sp, totalAcq, gsr, gp, lfInv, invInv, margin: sp > 0 ? gp / sp : 0 };
  }, [compareAnalysis]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4 pb-12" data-testid="deal-analysis-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Calculator className="w-5 h-5 text-muted-foreground" />
          <div>
            <div className="flex items-center gap-2 group">
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                onFocus={e => { if (e.target.value === 'Untitled Analysis') e.target.select(); }}
                className={`text-lg font-semibold h-8 border-0 border-b-2 border-dashed shadow-none rounded-none px-1 focus-visible:ring-0 transition-colors focus:border-[#C68E5D] ${name === 'Untitled Analysis' ? 'text-muted-foreground/60 border-muted-foreground/25' : 'border-transparent hover:border-muted-foreground/30'}`}
                placeholder="Enter deal name..."
                data-testid="analysis-name-input"
              />
            </div>
            <p className="text-[11px] text-muted-foreground -mt-0.5">Standalone deal feasibility analysis</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-sm gap-1.5 h-8 text-xs" onClick={() => setShowGuide(true)} data-testid="int-help-btn">
            <HelpCircle className="w-3.5 h-3.5" /> How to Use
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm gap-1.5 h-8 text-xs" onClick={() => setShowSaved(true)} data-testid="open-saved-btn">
            <FolderOpen className="w-3.5 h-3.5" /> Saved ({analyses.length})
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm gap-1.5 h-8 text-xs" onClick={handleNew} data-testid="new-analysis-btn">
            <Plus className="w-3.5 h-3.5" /> New
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm gap-1.5 h-8 text-xs" onClick={handleDownloadPDF} data-testid="download-pdf-btn">
            <Download className="w-3.5 h-3.5" /> PDF
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm gap-1.5 h-8 text-xs border-[#C68E5D] text-[#C68E5D] hover:bg-[#C68E5D]/10" onClick={handlePushToProposal} data-testid="push-to-proposal-btn">
            <FileText className="w-3.5 h-3.5" /> Push to Proposal
          </Button>
          <Button size="sm" className="rounded-sm gap-1.5 h-8 text-xs bg-[#1A2F23] hover:bg-[#2a4a38]" onClick={handleSave} disabled={saving} data-testid="save-analysis-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {activeId ? 'Save' : 'Save New'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT: Inputs */}
        <div className="space-y-3">
          <Section title="Acquisition Costs" icon={DollarSign}>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <CurrencyInput label="Purchase Price" value={deal.purchase_price} onChange={v => setDealField('purchase_price', v)} />
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total Acreage</label>
                  <input type="number" value={acreage} onChange={e => setAcreage(e.target.value)} placeholder="0" className="w-full mt-1 px-2.5 py-1.5 border rounded-sm text-sm" data-testid="int-acreage-input" />
                </div>
              </div>
              {(parseFloat(acreage) || 0) > 0 && calcs.pp > 0 && (
                <ReadOnlyField label="Purchase Price / Acre" value={fmt(calcs.pricePerAcrePurchase)} />
              )}
              <Separator />
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Due Diligence</p>
              <div className="grid grid-cols-3 gap-2">
                <CurrencyInput label="Survey" value={deal.survey} onChange={v => setDealField('survey', v)} />
                <CurrencyInput label="Soil" value={deal.soil} onChange={v => setDealField('soil', v)} />
                <CurrencyInput label="Other DD" value={deal.other_dd} onChange={v => setDealField('other_dd', v)} />
              </div>
              <ReadOnlyField label="Total Due Diligence" value={fmt(calcs.totalDD)} />
              <div className="grid grid-cols-2 gap-2">
                <CurrencyInput label="Purchase Closing Costs" value={deal.purchase_closing_costs} onChange={v => setDealField('purchase_closing_costs', v)} />
                <CurrencyInput label="Improvements" value={deal.improvements} onChange={v => setDealField('improvements', v)} />
              </div>
              <ReadOnlyField label="Total Acquisition Cost" value={fmt(calcs.totalAcquisitionCost)} accent />
            </div>
          </Section>

          <Section title="Sale & Revenue" icon={TrendingUp}>
            <div className="space-y-3">
              <CurrencyInput label="Estimated Sale Price" value={deal.sale_price} onChange={v => setDealField('sale_price', v)} />
              <div className="grid grid-cols-2 gap-2">
                <CurrencyInput label="Sale Closing Costs" value={deal.sale_closing_costs} onChange={v => setDealField('sale_closing_costs', v)} />
                <PercentInput label="Sale Commission %" value={deal.sale_commission_pct} onChange={v => setDealField('sale_commission_pct', v)} />
              </div>
              <ReadOnlyField label={`Sale Commission (${calcs.saleCommPct}%)`} value={fmt(calcs.saleCommission)} />
              <ReadOnlyField label="Gross Sales Revenue" value={fmt(calcs.grossSalesRevenue)} />
              <ReadOnlyField label="Gross Profit" value={fmt(calcs.grossProfit)} accent />
              {(parseFloat(acreage) || 0) > 0 && calcs.sp > 0 && (
                <ReadOnlyField label="Sale Price / Acre" value={fmt(calcs.pricePerAcreSale)} />
              )}
            </div>
          </Section>

          <Section title="Funding Structure" icon={BarChart3}>
            <div className="space-y-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Land Finder Funding %</p>
              <div className="grid grid-cols-2 gap-2">
                <PercentInput label="% of Purchase Price" value={funding.lf_pct_purchase} onChange={v => setFundingField('lf_pct_purchase', v)} />
                <ReadOnlyField label="LF Amount" value={fmt(calcs.lfPurchase)} />
                <PercentInput label="% of DD Costs" value={funding.lf_pct_dd} onChange={v => setFundingField('lf_pct_dd', v)} />
                <ReadOnlyField label="LF Amount" value={fmt(calcs.lfDD)} />
                <PercentInput label="% of Improvements" value={funding.lf_pct_improvements} onChange={v => setFundingField('lf_pct_improvements', v)} />
                <ReadOnlyField label="LF Amount" value={fmt(calcs.lfImprovements)} />
                <PercentInput label="% of Closing Costs" value={funding.lf_pct_closing} onChange={v => setFundingField('lf_pct_closing', v)} />
                <ReadOnlyField label="LF Amount" value={fmt(calcs.lfClosing)} />
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-2">
                <ReadOnlyField label="Total LF Investment" value={fmt(calcs.lfTotalInvestment)} />
                <ReadOnlyField label="Total Partner Investment" value={fmt(calcs.investorTotalInvestment)} accent />
              </div>
            </div>
          </Section>

          {tierWarnings.length > 0 && (
            <div className="p-2.5 rounded-sm bg-red-50 border border-red-200" data-testid="int-tier-warnings">
              <p className="text-[11px] font-semibold text-red-800 mb-1"><AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Tier Configuration Issues</p>
              {tierWarnings.map((w, i) => <p key={i} className="text-[10px] text-red-700">{w}</p>)}
            </div>
          )}

          <Section title="Scenario A — Profit Split Tiers" icon={BarChart3}>
            {calcs.investorTotalInvestment === 0 && optionATiers.some(t => (parseFloat(t.investor_split) || 0) > 0) && (
              <div className="mb-3 p-2.5 rounded-sm bg-amber-50 border border-amber-200 flex items-start sm:items-center justify-between gap-2 flex-col sm:flex-row">
                <p className="text-[11px] text-amber-800">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  LF is funding 100% — but partner still receives a profit share below.
                </p>
                <button onClick={() => setOptionATiers(prev => prev.map(t => ({ ...t, lf_split: 100, investor_split: 0 })))} className="px-2.5 py-1 text-[10px] font-medium bg-amber-100 hover:bg-amber-200 rounded-sm border border-amber-300 text-amber-900 whitespace-nowrap transition-colors" data-testid="int-scen-a-full-lf-btn">
                  Set LF to 100%
                </button>
              </div>
            )}
            <TierEditor tiers={optionATiers} setTiers={setOptionATiers} label="Scenario A" />
          </Section>

          <Section title="Scenario B — Profit Split Tiers" icon={BarChart3} defaultOpen={false}>
            {calcs.investorTotalInvestment === 0 && optionBTiers.some(t => (parseFloat(t.investor_split) || 0) > 0) && (
              <div className="mb-3 p-2.5 rounded-sm bg-amber-50 border border-amber-200 flex items-start sm:items-center justify-between gap-2 flex-col sm:flex-row">
                <p className="text-[11px] text-amber-800">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  LF is funding 100% — but partner still receives a profit share below.
                </p>
                <button onClick={() => setOptionBTiers(prev => prev.map(t => ({ ...t, lf_split: 100, investor_split: 0 })))} className="px-2.5 py-1 text-[10px] font-medium bg-amber-100 hover:bg-amber-200 rounded-sm border border-amber-300 text-amber-900 whitespace-nowrap transition-colors" data-testid="int-scen-b-full-lf-btn">
                  Set LF to 100%
                </button>
              </div>
            )}
            <TierEditor tiers={optionBTiers} setTiers={setOptionBTiers} label="Scenario B" />
          </Section>

          <Section title="Subdivide Analysis" icon={Layers} defaultOpen={isSubdivide}>
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isSubdivide} onChange={e => { setIsSubdivide(e.target.checked); if (e.target.checked && !parcels.length) addParcel(); }} className="rounded border-gray-300" data-testid="int-subdivide-toggle" />
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Enable Subdivide Mode</span>
              </label>
              {isSubdivide && (
                <>
                  <div className="flex items-center gap-3" data-testid="int-subdivide-mode-toggle">
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Allocate cost basis by:</span>
                    <div className="inline-flex rounded-sm border overflow-hidden">
                      <button type="button" onClick={() => setSubdivideMode('acres')}
                        className={`px-2.5 py-1 text-[11px] ${subdivideMode === 'acres' ? 'bg-[#1A2F23] text-white' : 'bg-white text-muted-foreground hover:bg-muted/50'}`}
                        data-testid="int-subdiv-mode-acres">Acres</button>
                      <button type="button" onClick={() => setSubdivideMode('percent')}
                        className={`px-2.5 py-1 text-[11px] ${subdivideMode === 'percent' ? 'bg-[#1A2F23] text-white' : 'bg-white text-muted-foreground hover:bg-muted/50'}`}
                        data-testid="int-subdiv-mode-pct">Percent</button>
                    </div>
                  </div>
                  {acresMismatch && subdivideMode === 'acres' && (
                    <div className="p-2 rounded-sm bg-amber-50 border border-amber-200" data-testid="int-acres-warning">
                      <p className="text-[10px] text-amber-800"><AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />
                        Parcel acres ({parcelAcresTotal.toFixed(2)}) {parcelAcresTotal > totalAcresNum ? 'exceed' : 'are less than'} total acreage ({totalAcresNum.toFixed(2)}). Cost allocation may be inaccurate.
                      </p>
                    </div>
                  )}
                  <div className="overflow-x-auto -mx-2">
                    <table className="w-full text-xs min-w-[750px]">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="text-left p-1.5 font-medium text-muted-foreground">Name</th>
                          <th className="text-left p-1.5 font-medium text-muted-foreground">Acres</th>
                          {subdivideMode === 'percent' && (
                            <th className="text-left p-1.5 font-medium text-muted-foreground">Pct%</th>
                          )}
                          <th className="text-left p-1.5 font-medium text-muted-foreground">$/Acre</th>
                          <th className="text-left p-1.5 font-medium text-muted-foreground">Sale Price</th>
                          <th className="text-left p-1.5 font-medium text-muted-foreground">Closing $</th>
                          <th className="text-left p-1.5 font-medium text-muted-foreground">Comm%</th>
                          <th className="text-left p-1.5 font-medium text-muted-foreground">Days</th>
                          <th className="text-center p-1.5 font-medium text-muted-foreground">JV</th>
                          <th className="w-6"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {parcels.map((p, i) => (
                          <tr key={i} className="border-b">
                            <td className="p-1"><input value={p.name} onChange={e => updateParcel(i, 'name', e.target.value)} className="w-20 px-1.5 py-1 border rounded-sm text-xs" /></td>
                            <td className="p-1"><input type="number" value={p.acres} onChange={e => updateParcel(i, 'acres', e.target.value)} className="w-14 px-1.5 py-1 border rounded-sm text-xs" placeholder="0" /></td>
                            {subdivideMode === 'percent' && (
                              <td className="p-1"><input type="number" value={p.pct} onChange={e => updateParcel(i, 'pct', e.target.value)} className="w-14 px-1.5 py-1 border rounded-sm text-xs" placeholder="0" /></td>
                            )}
                            <td className="p-1"><input type="number" value={p.price_per_acre} onChange={e => updateParcel(i, 'price_per_acre', e.target.value)} className="w-20 px-1.5 py-1 border rounded-sm text-xs" placeholder="0" /></td>
                            <td className="p-1"><input type="number" value={p.sale_price} onChange={e => updateParcel(i, 'sale_price', e.target.value)} className="w-24 px-1.5 py-1 border rounded-sm text-xs" placeholder="0" /></td>
                            <td className="p-1"><input type="number" value={p.closing_costs} onChange={e => updateParcel(i, 'closing_costs', e.target.value)} className="w-16 px-1.5 py-1 border rounded-sm text-xs" placeholder="auto" /></td>
                            <td className="p-1"><input type="number" value={p.commission_pct} onChange={e => updateParcel(i, 'commission_pct', e.target.value)} className="w-12 px-1.5 py-1 border rounded-sm text-xs" placeholder={String(calcs.saleCommPct || 6)} /></td>
                            <td className="p-1"><input type="number" value={p.days_held} onChange={e => updateParcel(i, 'days_held', e.target.value)} className="w-14 px-1.5 py-1 border rounded-sm text-xs" placeholder="0" /></td>
                            <td className="p-1 text-center"><input type="checkbox" checked={p.is_jv} onChange={e => updateParcel(i, 'is_jv', e.target.checked)} className="rounded border-gray-300" /></td>
                            <td className="p-1"><button onClick={() => removeParcel(i)} className="p-0.5 text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button onClick={addParcel} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="int-add-parcel-btn">
                    <Plus className="w-3 h-3" /> Add Parcel
                  </button>
                </>
              )}
            </div>
          </Section>

          <Section title="Notes" icon={FileText} defaultOpen={false}>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Deal notes, assumptions, risk factors..." rows={3} className="text-sm rounded-sm" />
          </Section>

          <Section title="Goal Seek" icon={Target} defaultOpen={false}>
            <div className="space-y-3">
              {isSubdivide && (
                <div className="p-2 rounded-sm bg-blue-50 border border-blue-200" data-testid="int-goal-seek-subdiv-note">
                  <p className="text-[10px] text-blue-800"><AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />
                    Goal Seek solves based on the single-sale scenario. In Subdivide mode, each parcel has its own sale price — apply results to the main deal inputs before subdividing.
                  </p>
                </div>
              )}
              <p className="text-[10px] text-muted-foreground">Find the sale price or purchase price needed to hit your target.</p>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">What are you targeting?</label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {[
                    { key: 'margin', label: 'Profit Margin %' },
                    { key: 'coc', label: 'Cash-on-Cash Return %' },
                    { key: 'profit', label: 'Profit Amount' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => setGoalType(opt.key)}
                      className={`px-2.5 py-1 rounded-sm text-[11px] font-medium border transition-colors ${goalType === opt.key ? 'bg-[#1A2F23] text-white border-[#1A2F23]' : 'bg-white text-muted-foreground border-border hover:bg-muted/50'}`}
                      data-testid={`int-goal-type-${opt.key}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {goalType === 'coc' && optionATiers.length > 1 && (
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Which tier to solve against?</label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {optionATiers.map((t, i) => (
                      <button
                        key={i}
                        onClick={() => setGoalTierIndex(i)}
                        className={`px-2.5 py-1 rounded-sm text-[11px] font-medium border transition-colors ${goalTierIndex === i ? 'bg-[#1A2F23] text-white border-[#1A2F23]' : 'bg-white text-muted-foreground border-border hover:bg-muted/50'}`}
                        data-testid={`int-goal-tier-${i}`}
                      >
                        Tier {i + 1} ({t.investor_split}%)
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                {goalType === 'profit' ? (
                  <CurrencyInput label="Target Profit" value={goalValue} onChange={setGoalValue} />
                ) : (
                  <PercentInput
                    label={goalType === 'margin' ? 'Target Margin %' : 'Target Cash-on-Cash Return %'}
                    value={goalValue}
                    onChange={setGoalValue}
                  />
                )}
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Solve for</label>
                <div className="flex gap-1.5 mt-1.5">
                  {[
                    { key: 'sale_price', label: 'Required Sale Price' },
                    { key: 'purchase_price', label: 'Max Purchase Price' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => setSolveFor(opt.key)}
                      className={`px-2.5 py-1 rounded-sm text-[11px] font-medium border transition-colors ${solveFor === opt.key ? 'bg-[#C68E5D] text-white border-[#C68E5D]' : 'bg-white text-muted-foreground border-border hover:bg-muted/50'}`}
                      data-testid={`int-solve-for-${opt.key}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {goalResult && (
                <div className={`mt-2 p-3 rounded-sm border-2 ${goalResult.error ? 'border-red-200 bg-red-50' : 'border-[#1A2F23]/20 bg-[#1A2F23]/5'}`}>
                  {goalResult.error ? (
                    <p className="text-xs text-red-600 font-medium">{goalResult.error}</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{goalResult.label}</p>
                          <p className="text-xl font-bold font-mono text-[#1A2F23] mt-0.5" data-testid="int-goal-result-value">{fmt(goalResult.value)}</p>
                        </div>
                        <button
                          onClick={applyGoalResult}
                          className="px-3 py-1.5 rounded-sm text-[11px] font-medium bg-[#1A2F23] text-white hover:bg-[#2a4a38] transition-colors"
                          data-testid="int-goal-apply-btn"
                        >
                          Apply to Deal
                        </button>
                      </div>
                      {goalSummary && (
                        <div className="pt-2 border-t border-[#1A2F23]/10 grid grid-cols-2 gap-x-4 gap-y-1">
                          <div className="flex justify-between text-[11px]"><span className="text-muted-foreground">Gross Profit</span><span className="font-mono font-medium">{fmt(goalSummary.gp)}</span></div>
                          <div className="flex justify-between text-[11px]"><span className="text-muted-foreground">Margin</span><span className="font-mono font-medium">{fmtPct(goalSummary.margin)}</span></div>
                          <div className="flex justify-between text-[11px]"><span className="text-muted-foreground">LF Profit</span><span className="font-mono font-medium">{fmt(goalSummary.lfProfit)}</span></div>
                          <div className="flex justify-between text-[11px]"><span className="text-muted-foreground">Partner Profit</span><span className="font-mono font-medium">{fmt(goalSummary.partnerProfit)}</span></div>
                          <div className="flex justify-between text-[11px]"><span className="text-muted-foreground">Partner CoC</span><span className="font-mono font-medium">{fmtPct(goalSummary.coc)}</span></div>
                          <div className="flex justify-between text-[11px]"><span className="text-muted-foreground">Partner Inv.</span><span className="font-mono font-medium">{fmt(goalSummary.partnerInv)}</span></div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Section>
        </div>

        {/* RIGHT: Results */}
        <div className="space-y-3">
          {/* Financial Summary */}
          <Card className="rounded-sm border-2 border-[#1A2F23]/20">
            <CardHeader className="py-2.5 px-4 bg-[#1A2F23] text-white rounded-t-sm">
              <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Financial Summary</CardTitle>
            </CardHeader>
            <CardContent className="px-4 py-3 space-y-0.5">
              {isSubdivide && parcelResults.length > 0 && (
                <div className="mb-2 p-1.5 rounded-sm bg-blue-50/80 border border-blue-100">
                  <p className="text-[9px] text-blue-700 leading-tight">Below reflects the single-sale scenario. See <strong>Subdivide Summary</strong> for per-parcel totals.</p>
                </div>
              )}
              <StatLine label="Purchase Price" value={fmt(calcs.pp)} />
              <StatLine label="Total Due Diligence" value={fmt(calcs.totalDD)} />
              <StatLine label="Purchase Closing" value={fmt(calcs.purchaseClosing)} />
              <StatLine label="Improvements" value={fmt(calcs.improvements)} />
              <StatLine label="Total Acquisition Cost" value={fmt(calcs.totalAcquisitionCost)} bold />
              <Separator className="my-1" />
              <StatLine label="LF Investment" value={fmt(calcs.lfTotalInvestment)} />
              <StatLine label="Partner Investment" value={fmt(calcs.investorTotalInvestment)} bold />
              <Separator className="my-1" />
              <StatLine label="Sale Price" value={fmt(calcs.sp)} />
              <StatLine label="Sale Closing" value={`-${fmt(calcs.saleClosing)}`} />
              <StatLine label={`Commission (${calcs.saleCommPct}%)`} value={`-${fmt(calcs.saleCommission)}`} />
              <StatLine label="Gross Sales Revenue" value={fmt(calcs.grossSalesRevenue)} bold />
              <Separator className="my-1" />
              <StatLine label="Gross Profit" value={fmt(calcs.grossProfit)} accent bold />
              <StatLine label="Margin" value={fmtPct(calcs.margin)} />
              {(parseFloat(acreage) || 0) > 0 && (
                <>
                  <Separator className="my-1" />
                  {calcs.pp > 0 && <StatLine label="Purchase $/Acre" value={fmt(calcs.pricePerAcrePurchase)} />}
                  {calcs.sp > 0 && <StatLine label="Sale $/Acre" value={fmt(calcs.pricePerAcreSale)} />}
                </>
              )}
            </CardContent>
          </Card>

          {isSubdivide && parcelResults.length > 0 && (
            <Card className="rounded-sm border-2 border-[#C68E5D]/30" data-testid="int-subdivide-summary">
              <CardHeader className="py-2.5 px-4 bg-[#C68E5D] text-white rounded-t-sm">
                <CardTitle className="text-sm flex items-center gap-2"><Layers className="w-4 h-4" /> Subdivide Summary</CardTitle>
              </CardHeader>
              <CardContent className="px-4 py-3 space-y-0.5">
                <StatLine label="Total Parcels" value={String(parcelResults.length)} />
                <StatLine label="Total Acres" value={subdivSummary?.totalAcres?.toFixed(2) || '0'} />
                <StatLine label="Total Sale Revenue" value={fmt(subdivSummary?.totalRevenue || 0)} />
                <StatLine label="Total Closing Costs" value={fmt(subdivSummary?.totalClosing || 0)} />
                <Separator className="my-1" />
                <StatLine label="Total Gross Profit" value={fmt(subdivSummary?.totalGP || 0)} accent bold />
                <StatLine label="Total LF Profit" value={fmt(subdivSummary?.totalLF || 0)} bold />
                <StatLine label="Total Partner Profit" value={fmt(subdivSummary?.totalPartner || 0)} />
                <Separator className="my-1" />
                {parcelResults.map((r, i) => (
                  <div key={i} className="flex justify-between text-[11px] py-0.5">
                    <span className="text-muted-foreground">{r.name} {r.is_jv ? '' : '(No JV)'}</span>
                    <span className="font-mono">{fmt(r.grossProfit)} <span className="text-muted-foreground ml-1">({r.tierLabel})</span></span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Scenario A */}
          {isSubdivide && parcelResults.length > 0 && parcelResultsB.length > 0 && (
            <Card className="rounded-sm border-2 border-[#1A2F23]/15" data-testid="int-scenario-comparison">
              <CardHeader className="py-2.5 px-4 bg-gradient-to-r from-[#1A2F23] to-[#C68E5D] text-white rounded-t-sm">
                <CardTitle className="text-sm flex items-center gap-2"><Columns className="w-4 h-4" /> Scenario Comparison</CardTitle>
              </CardHeader>
              <CardContent className="px-0 py-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-muted/40 text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Parcel</th>
                        <th className="text-right px-2 py-2 font-medium">Acres</th>
                        <th className="text-right px-2 py-2 font-medium">Days</th>
                        <th className="text-center px-2 py-2 font-medium border-l bg-[#1A2F23]/5" colSpan="3">Scenario A</th>
                        <th className="text-center px-2 py-2 font-medium border-l bg-[#C68E5D]/10" colSpan="3">Scenario B</th>
                      </tr>
                      <tr className="border-t text-[10px]">
                        <th colSpan="3"></th>
                        <th className="text-right px-2 py-1 font-medium border-l bg-[#1A2F23]/5">Tier</th>
                        <th className="text-right px-2 py-1 font-medium bg-[#1A2F23]/5">LF Profit</th>
                        <th className="text-right px-2 py-1 font-medium bg-[#1A2F23]/5">Partner</th>
                        <th className="text-right px-2 py-1 font-medium border-l bg-[#C68E5D]/10">Tier</th>
                        <th className="text-right px-2 py-1 font-medium bg-[#C68E5D]/10">LF Profit</th>
                        <th className="text-right px-2 py-1 font-medium bg-[#C68E5D]/10">Partner</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parcelResults.map((rA, i) => {
                        const rB = parcelResultsB[i];
                        return (
                          <tr key={i} className="border-t">
                            <td className="px-3 py-1.5 font-medium">{rA.name}{!rA.is_jv ? ' (No JV)' : ''}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{rA.pAcres}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{rA.days_held || '—'}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-muted-foreground border-l bg-[#1A2F23]/5">{rA.tierLabel}</td>
                            <td className="px-2 py-1.5 text-right font-mono bg-[#1A2F23]/5">{fmt(rA.lfProfit)}</td>
                            <td className="px-2 py-1.5 text-right font-mono bg-[#1A2F23]/5">{fmt(rA.partnerProfit)}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-muted-foreground border-l bg-[#C68E5D]/10">{rB?.tierLabel || '—'}</td>
                            <td className="px-2 py-1.5 text-right font-mono bg-[#C68E5D]/10">{fmt(rB?.lfProfit || 0)}</td>
                            <td className="px-2 py-1.5 text-right font-mono bg-[#C68E5D]/10">{fmt(rB?.partnerProfit || 0)}</td>
                          </tr>
                        );
                      })}
                      <tr className="border-t-2 font-bold bg-muted/30">
                        <td className="px-3 py-2" colSpan="3">Totals</td>
                        <td className="px-2 py-2 border-l bg-[#1A2F23]/5"></td>
                        <td className="px-2 py-2 text-right font-mono bg-[#1A2F23]/5">{fmt(subdivSummary?.totalLF || 0)}</td>
                        <td className="px-2 py-2 text-right font-mono bg-[#1A2F23]/5">{fmt(subdivSummary?.totalPartner || 0)}</td>
                        <td className="px-2 py-2 border-l bg-[#C68E5D]/10"></td>
                        <td className="px-2 py-2 text-right font-mono bg-[#C68E5D]/10">{fmt(subdivSummaryB?.totalLF || 0)}</td>
                        <td className="px-2 py-2 text-right font-mono bg-[#C68E5D]/10">{fmt(subdivSummaryB?.totalPartner || 0)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {subdivSummary && subdivSummaryB && (
                  <div className="px-4 py-2.5 border-t bg-muted/20 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground font-medium">LF Profit Advantage:</span>
                    <span className={`font-mono font-bold ${(subdivSummary.totalLF - subdivSummaryB.totalLF) >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {(subdivSummary.totalLF - subdivSummaryB.totalLF) >= 0 ? 'Scenario A' : 'Scenario B'} by {fmt(Math.abs(subdivSummary.totalLF - subdivSummaryB.totalLF))}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <ResultsTable results={optionAResults} title="Scenario A — Partner & LF Returns" investorCapital={calcs.investorTotalInvestment} />
          {/* Scenario B */}
          <ResultsTable results={optionBResults} title="Scenario B — Partner & LF Returns" investorCapital={calcs.investorTotalInvestment} />

          {/* Compare Panel */}
          {compareCalcs && compareAnalysis && (
            <Card className="rounded-sm border-2 border-amber-300/50">
              <CardHeader className="py-2.5 px-4 bg-amber-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2"><Columns className="w-4 h-4" /> Compare: {compareAnalysis.name}</CardTitle>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setCompareId(null)}><X className="w-3 h-3" /></Button>
                </div>
              </CardHeader>
              <CardContent className="px-4 py-3 text-xs">
                <div className="grid grid-cols-3 gap-2 font-medium text-muted-foreground mb-1.5">
                  <span></span><span className="text-right">Current</span><span className="text-right">Compare</span>
                </div>
                {[
                  ['Purchase Price', calcs.pp, compareCalcs.pp],
                  ['Sale Price', calcs.sp, compareCalcs.sp],
                  ['Gross Profit', calcs.grossProfit, compareCalcs.gp],
                  ['LF Investment', calcs.lfTotalInvestment, compareCalcs.lfInv],
                  ['Partner Investment', calcs.investorTotalInvestment, compareCalcs.invInv],
                ].map(([label, curr, comp], i) => (
                  <div key={i} className="grid grid-cols-3 gap-2 items-center py-1 border-b last:border-0">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="text-right font-mono">{fmt(curr)}</span>
                    <span className="text-right font-mono">{fmt(comp)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Saved Dialog */}
      {/* PDF Scenario Selection Dialog */}
      <Dialog open={showPdfDialog} onOpenChange={setShowPdfDialog}>
        <DialogContent className="sm:max-w-xs" data-testid="da-pdf-scenario-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Download Analysis PDF</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Select which scenario(s) to include:</p>
          <div className="space-y-3 py-2">
            {['A', 'B'].map(s => (
              <label
                key={s}
                className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition-colors ${pdfScenarios.includes(s) ? 'border-[#1A2F23] bg-[#1A2F23]/5' : 'hover:bg-muted/30'}`}
                data-testid={`da-pdf-scenario-option-${s}`}
              >
                <Checkbox
                  checked={pdfScenarios.includes(s)}
                  onCheckedChange={(checked) => {
                    setPdfScenarios(prev => checked ? [...prev, s] : prev.filter(x => x !== s));
                  }}
                />
                <div>
                  <p className="text-sm font-medium">Scenario {s}</p>
                  <p className="text-xs text-muted-foreground">{s === 'A' ? 'Option A profit split tiers' : 'Option B profit split tiers'}</p>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setShowPdfDialog(false)}>Cancel</Button>
            <Button
              size="sm"
              className="rounded-sm bg-[#1A2F23] hover:bg-[#1A2F23]/90 text-white"
              onClick={confirmDownloadPDF}
              disabled={pdfScenarios.length === 0}
              data-testid="da-pdf-confirm-download-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showSaved} onOpenChange={setShowSaved}>
        <DialogContent className="sm:max-w-lg max-h-[70vh] overflow-y-auto" data-testid="saved-analyses-dialog">
          <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><FolderOpen className="w-5 h-5" /> Saved Analyses</DialogTitle></DialogHeader>
          {analyses.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No saved analyses yet.</p>
          ) : (
            <div className="space-y-2">
              {analyses.map(a => {
                const d = a.deal || {};
                return (
                  <div key={a.id} className={`border rounded-sm p-3 hover:bg-muted/30 transition-colors ${a.id === activeId ? 'border-[#1A2F23] bg-[#1A2F23]/5' : ''}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => loadAnalysis(a)}>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{a.name}</span>
                          {a.id === activeId && <Badge className="text-[9px] bg-[#1A2F23] text-white">Active</Badge>}
                        </div>
                        <div className="flex gap-3 text-[11px] text-muted-foreground mt-0.5">
                          <span>Buy: {fmt(parseFloat(d.purchase_price) || 0)}</span>
                          <span>Sell: {fmt(parseFloat(d.sale_price) || 0)}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {new Date(a.updated_at).toLocaleDateString()}
                          {a.created_by && <span> · by {a.created_by}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Compare" onClick={() => { setCompareId(a.id); setShowSaved(false); }}><Columns className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Duplicate" onClick={() => handleDuplicate(a.id)}><Copy className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" title="Delete" onClick={() => handleDelete(a.id)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <HowToUseGuide open={showGuide} onOpenChange={setShowGuide} />
    </div>
  );
}
